import { Action, Reducer } from 'redux';
import { GlobalState, AppThunkAction } from '.';
import { ActionDefinition, createDate, createMessage, DefaultActionDefinition, Image, ImageCollection, Folder, StorageScheme } from '../models';
import { produce } from 'immer';
import moment from 'moment';
import * as MessageStore from './Message';
import { getImages, getImagesForAncestors, upload, moveImages, deleteImages } from '../services/Api.Image.Service';
import { getAccessToken } from '../auth/service';
import { getParentFoldersForImage } from '../services/util';
import _ from 'lodash';

function getAppOrigin(): string {
    if (typeof window === 'undefined' || !window.location || !window.location.origin) {
        return '';
    }

    return window.location.origin;
}

function toAbsoluteUrl(url: string): string {
    if (!url) {
        return url;
    }

    const origin = getAppOrigin();
    if (!origin) {
        return url;
    }

    try {
        return new URL(url, origin).toString();
    } catch {
        return url;
    }
}

function toRelativeUrl(url: string): string {
    if (!url) {
        return url;
    }

    const origin = getAppOrigin();
    if (!origin) {
        return url;
    }

    try {
        const parsedUrl = new URL(url, origin);
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
        return url;
    }
}

function normalizeImage(image: Image): Image {
    const fallbackRawUrl = `/api/image/${image.id}.jpg`;
    const fallbackUrl = `/image/${image.id}`;
    const rawUrl = toRelativeUrl(image.rawUrl) || fallbackRawUrl;

    return {
        ...image,
        url: toRelativeUrl(image.url) || fallbackUrl,
        rawUrl,
        resizeUrl: toRelativeUrl(image.resizeUrl) || fallbackRawUrl,
        referenceRawUrl: toAbsoluteUrl(rawUrl) || rawUrl
    };
}

function normalizeImageCollections(collections: ImageCollection[]): ImageCollection[] {
    return collections.map(collection => ({
        ...collection,
        images: (collection.images || []).map(normalizeImage)
    }));
}

export enum ActionTypes {
    LoadImagesStart = '@@images/LOAD_IMAGES/START',
    LoadImagesComplete = '@@images/LOAD_IMAGES/COMPLETE',
    LoadImagesFailed = '@@images/LOAD_IMAGES/FAILED',
    InitializeStart = '@@images/INITALIZE/START',
    InitializeComplete = '@@images/INITALIZE/COMPLETE',
    InitializeFailed = '@@images/INITALIZE/FAILED',
    UploadStart = '@@images/UPLOAD/START',
    UploadComplete = '@@images/UPLOAD/COMPLETE',
    UploadFailed = '@@images/UPLOAD/FAILED',
    MoveImagesStart = '@@images/MOVE/START',
    MoveImagesComplete = '@@images/MOVE/COMPLETE',
    MoveImagesFailed = '@@images/MOVE/FAILED',
    DeleteImagesStart = '@@images/DELETE/START',
    DeleteImagesComplete = '@@images/DELETE/COMPLETE',
    DeleteImagesFailed = '@@images/DELETE/FAILED'
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface ImageState {
    loadImages: ActionDefinition[];
    initialize: ActionDefinition;
    images: ImageCollection[];
    upload: ActionDefinition;
    moveImages: ActionDefinition;
    deleteImages: ActionDefinition;
}

export const initialState: ImageState = {
    loadImages: [],
    initialize: DefaultActionDefinition,
    images: [],
    upload: DefaultActionDefinition,
    moveImages: DefaultActionDefinition,
    deleteImages: DefaultActionDefinition
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface LoadImagesStart { type: ActionTypes.LoadImagesStart, folderId: string }
export interface LoadImagesComplete { type: ActionTypes.LoadImagesComplete, folderId: string, images: Image[] }
export interface LoadImagesFailed { type: ActionTypes.LoadImagesFailed, folderId: string, error: Error | null }
export interface InitializeStart { type: ActionTypes.InitializeStart, activeNodeId: string }
export interface InitializeComplete { type: ActionTypes.InitializeComplete, images: ImageCollection[] }
export interface InitializeFailed { type: ActionTypes.InitializeFailed, error: Error | null }
export interface UploadStart { type: ActionTypes.UploadStart, parentFolderId: string }
export interface UploadComplete { type: ActionTypes.UploadComplete, images: ImageCollection[] }
export interface UploadFailed { type: ActionTypes.UploadFailed, error: Error | null }
export interface MoveImagesStart { type: ActionTypes.MoveImagesStart }
export interface MoveImagesComplete { type: ActionTypes.MoveImagesComplete, images: ImageCollection[] }
export interface MoveImagesFailed { type: ActionTypes.MoveImagesFailed, error: Error | null }
export interface DeleteImagesStart { type: ActionTypes.DeleteImagesStart }
export interface DeleteImagesComplete { type: ActionTypes.DeleteImagesComplete, images: ImageCollection[] }
export interface DeleteImagesFailed { type: ActionTypes.DeleteImagesFailed, error: Error | null }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type Actions =
    | LoadImagesStart
    | LoadImagesComplete
    | LoadImagesFailed
    | InitializeStart
    | InitializeComplete
    | InitializeFailed
    | UploadStart
    | UploadComplete
    | UploadFailed
    | MoveImagesStart
    | MoveImagesComplete
    | MoveImagesFailed
    | DeleteImagesStart
    | DeleteImagesComplete
    | DeleteImagesFailed;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    loadImages: (folderId: string): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.LoadImagesStart, folderId });
        try {
            const accessToken = await getAccessToken();
            const odata = await getImages(accessToken, folderId);
            const images = (odata.value || []).map(normalizeImage);
            dispatch({ type: ActionTypes.LoadImagesComplete, images, folderId });
        } catch (err) {
            const error = err as any;
            dispatch({ type: ActionTypes.LoadImagesFailed, folderId, error: error.message });
            dispatch(MessageStore.actionCreators.show(createMessage('Error Loading Images', error.message, error)));
        }
    },
    initialize: (activeNodeId: string, onComplete: (images: ImageCollection[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        /// loads all images in the selected directory and all ancestor directories
        dispatch({ type: ActionTypes.InitializeStart, activeNodeId });
        try {
            const accessToken = await getAccessToken();
            const imageCollections = normalizeImageCollections(await getImagesForAncestors(accessToken, activeNodeId));
            dispatch({ type: ActionTypes.InitializeComplete, images: imageCollections });
            if (onComplete) {
                onComplete(imageCollections);
            }
        } catch (err) {
            const error = err as any;
            dispatch({ type: ActionTypes.InitializeFailed, error: error.message });
            dispatch(MessageStore.actionCreators.show(createMessage('Error Initializing Images', error.message, error)));
        }
    },
    upload: (storageScheme: StorageScheme, parentFolderId: string, files: File[], onComplete: (images: ImageCollection[]) => void, onError: (error: string) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        /// loads all images in the selected directory and all ancestor directories
        dispatch({ type: ActionTypes.UploadStart, parentFolderId });
        try {
            const accessToken = await getAccessToken();
            const imageCollections = normalizeImageCollections(await upload(accessToken, parentFolderId, files, storageScheme));
            dispatch({ type: ActionTypes.UploadComplete, images: imageCollections });
            if (onComplete) {
                onComplete(imageCollections);
            }
        } catch (err) {
            const error = err as any;
            dispatch({ type: ActionTypes.InitializeFailed, error: error.message });
            if (onError) {
                onError(error.message);
            } else {
                dispatch(MessageStore.actionCreators.show(createMessage('Error Uploading Images', error.message, error)));
            }
        }
    },
    moveImages: (images: Image[], newFolderId?: string, onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.MoveImagesStart });
        try {
            const accessToken = await getAccessToken();
            const imageIds = images.map(i => i.id);
            const updatedImageCollections = normalizeImageCollections(await moveImages(accessToken, imageIds, newFolderId));
            dispatch({ type: ActionTypes.MoveImagesComplete, images: updatedImageCollections });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.MoveImagesFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Moving Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    },
    deleteImages: (images: Image[], onComplete?: (success: boolean, errors?: string[]) => void): AppThunkAction<Actions | MessageStore.Actions> => async (dispatch, getState) => {
        dispatch({ type: ActionTypes.DeleteImagesStart });
        try {
            const accessToken = await getAccessToken();
            const imageIds = images.map(i => i.id);
            const updatedImageCollections = normalizeImageCollections(await deleteImages(accessToken, imageIds));
            dispatch({ type: ActionTypes.DeleteImagesComplete, images: updatedImageCollections });
            if (onComplete) {
                onComplete(true);
            }
        } catch (error) {
            const err = error as any;
            dispatch({ type: ActionTypes.DeleteImagesFailed, error: err.message });
            //dispatch(MessageStore.actionCreators.show(createMessage('Error Deleting Folder', err.message, err)));
            if (onComplete) {
                onComplete(false, [err.message]);
            }
        }
    }
};

// ----------------
// UTILS - These are functions that aid in manipulating data from the current store

export const utils = {
    getImagesForFolder: (images: ImageCollection[], folderId: string) => {
        const collection = images.find(collection => collection.id === folderId);
        return collection ? collection.images : [];
    },
    getParentFolders: (allFolders: Folder[], image: Image) => getParentFoldersForImage(allFolders, image)
}


// ----------------
// SELECTORS - These are functions that, given the application state, will access a particular piece of information
// from the current store

export const selectors = {
    // imagesForFolder: (folderId: string) => (state: GlobalState) => {
    //     const collection = state.image.images.find(ic => ic.id === folderId);
    //     return collection ? collection.images : [];
    // },
    // loadImagesAction: (folderId: string) => (state: GlobalState) => state.image.loadImages.find(li => li.id === folderId) || DefaultActionDefinition,
    loadImages: (state: GlobalState) => state.image.loadImages,
    images: (state: GlobalState) => state.image.images,
    imagesFlattened: (state: GlobalState) => _.flatten(state.image.images.map(i => i.images)),
    initialize: (state: GlobalState) => state.image.initialize,
    upload: (state: GlobalState) => state.image.upload,
    byId: (id: string) => (state: GlobalState) => _.flatten(state.image.images.map(i => i.images)).find(i => i.id.toLowerCase() === id.toLowerCase())
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<ImageState> = (state: ImageState | undefined, incomingAction: Action): ImageState => {
    if (state === undefined) {
        return initialState;
    }

    const getLoadImagesAction = (draft: ImageState, folderId: string) => {
        let actionDef = draft.loadImages.find(li => li.id === folderId);
        if (!actionDef) {
            actionDef = { ...DefaultActionDefinition, id: folderId } as ActionDefinition;
            draft.loadImages.push(actionDef);
        }
        return actionDef;
    }

    const getImages = (draft: ImageState, folderId: string) => {
        let collection = draft.images.find(ic => ic.id === folderId);
        if (!collection) {
            collection = { id: folderId, images: [] } as ImageCollection;
            draft.images.push(collection);
        }
        return collection;
    }

    const action = incomingAction as Actions;
    switch (action.type) {
        case ActionTypes.LoadImagesStart: {
            const newState = produce(state, draft => {
                const actionDef = getLoadImagesAction(draft, action.folderId);
                actionDef.is = true;
                actionDef.error = null;
                const collection = getImages(draft, action.folderId);
                collection.images = [];
            });
            return newState;
        }
        case ActionTypes.LoadImagesFailed: {
            const newState = produce(state, draft => {
                const actionDef = getLoadImagesAction(draft, action.folderId);
                actionDef.is = false;
                actionDef.did = createDate(moment().utc());
                actionDef.error = action.error;
            });
            return newState;
        }
        case ActionTypes.LoadImagesComplete: {
            const newState = produce(state, draft => {
                const actionDef = getLoadImagesAction(draft, action.folderId);
                actionDef.is = false;
                actionDef.did = createDate(moment().utc());
                const collection = getImages(draft, action.folderId);
                collection.images = action.images;
            });
            return newState;
        }
        case ActionTypes.InitializeStart: {
            const newState = produce(state, draft => {
                draft.initialize.is = true;
                draft.initialize.error = initialState.initialize.error;
            });
            return newState;
        }
        case ActionTypes.InitializeFailed: {
            const newState = produce(state, draft => {
                draft.initialize.is = false;
                draft.initialize.did = createDate(moment().utc());
                draft.initialize.error = action.error;
            });
            return newState;
        }
        case ActionTypes.InitializeComplete: {
            const newState = produce(state, draft => {
                draft.initialize.is = true;
                draft.initialize.did = createDate(moment().utc());
                const folderIdsLoaded = action.images.map(i => i.id);
                draft.images = draft.images.filter(i => !folderIdsLoaded.includes(i.id));
                draft.images.push.apply(draft.images, action.images);
            });
            return newState;
        }
        case ActionTypes.UploadStart: {
            const newState = produce(state, draft => {
                draft.upload.is = true;
                draft.upload.error = initialState.upload.error;
            });
            return newState;
        }
        case ActionTypes.UploadFailed: {
            const newState = produce(state, draft => {
                draft.upload.is = false;
                draft.upload.did = createDate(moment().utc());
                draft.upload.error = action.error;
            });
            return newState;
        }
        case ActionTypes.UploadComplete: {
            const newState = produce(state, draft => {
                draft.upload.is = true;
                draft.upload.did = createDate(moment().utc());
                const folderIdsLoaded = action.images.map(i => i.id);
                draft.images = draft.images.filter(i => !folderIdsLoaded.includes(i.id));
                draft.images.push.apply(draft.images, action.images);
            });
            return newState;
        }
        case ActionTypes.MoveImagesStart: {
            const newState = produce(state, draft => {
                draft.moveImages.is = true;
                draft.moveImages.error = initialState.upload.error;
            });
            return newState;
        }
        case ActionTypes.MoveImagesFailed: {
            const newState = produce(state, draft => {
                draft.moveImages.is = false;
                draft.moveImages.did = createDate(moment().utc());
                draft.moveImages.error = action.error;
            });
            return newState;
        }
        case ActionTypes.MoveImagesComplete: {
            const newState = produce(state, draft => {
                draft.moveImages.is = true;
                draft.moveImages.did = createDate(moment().utc());
                const folderIdsLoaded = action.images.map(i => i.id);
                draft.images = draft.images.filter(i => !folderIdsLoaded.includes(i.id));
                draft.images.push.apply(draft.images, action.images);
            });
            return newState;
        }
        case ActionTypes.DeleteImagesStart: {
            const newState = produce(state, draft => {
                draft.deleteImages.is = true;
                draft.deleteImages.error = initialState.upload.error;
            });
            return newState;
        }
        case ActionTypes.DeleteImagesFailed: {
            const newState = produce(state, draft => {
                draft.deleteImages.is = false;
                draft.deleteImages.did = createDate(moment().utc());
                draft.deleteImages.error = action.error;
            });
            return newState;
        }
        case ActionTypes.DeleteImagesComplete: {
            const newState = produce(state, draft => {
                draft.deleteImages.is = true;
                draft.deleteImages.did = createDate(moment().utc());
                const folderIdsLoaded = action.images.map(i => i.id);
                draft.images = draft.images.filter(i => !folderIdsLoaded.includes(i.id));
                draft.images.push.apply(draft.images, action.images);
            });
            return newState;
        }
        default: {
            return state;
        }
    }
};